/**
 * Raw input state. Everything lands in one mutable struct that systems poll —
 * no events fired into game code, no per-frame allocation.
 *
 * Lineage II's scheme: the cursor is never captured. Left click issues an
 * order (move here, target that), right-drag orbits the camera, the wheel
 * zooms. That is why there is no pointer lock anywhere in here — the HUD has to
 * stay clickable at all times, and a captured pointer has no screen position to
 * pick with.
 */

export const input = {
    // Accumulated mouse delta while the right button is held, in radians.
    lookX: 0,
    lookY: 0,

    // Zoom, consumed by the camera rig.
    zoomDelta: 0,

    /** Right button held — the camera is being orbited. */
    orbiting: false,

    /** Snow-surf, held on `R` now that the right button drives the camera. */
    surf: false,

    /** Set on left click over the canvas, cleared once the game consumes it. */
    clickPending: false,
    clickX: 0,
    clickY: 0,
    /** True when the pending click was a double click. */
    clickDouble: false,

    /** @type {number} 0 = none, else 1..12 — hotbar slot pressed this frame. */
    hotbarPressed: 0,

    /** @type {number} 0 = none, else 1..5 — raw spell debug keys. */
    spellPressed: 0,
    /** @type {boolean} spell 2 (Ribbon) is a held cast */
    spellHeld2: false,
};

const keys = Object.create(null);

const LOOK_SCALE = 0.0022;

/** @type {{[k:string]: (() => void)|undefined}} */
let hooks = {};

/** True while the event came from a text field, where keys mean letters. */
function typing(e) {
    const t = e.target;
    return !!t && typeof t.closest === "function" && !!t.closest("input, textarea, [contenteditable]");
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {{
 *   onToggleOverlay?: () => void,
 *   onModal?: (name: string) => void,
 *   onEscape?: () => void,
 *   onChat?: () => void,
 * }} [handlers]
 */
export function initInput(canvas, handlers) {
    hooks = handlers ?? {};

    // Pointer events, not mouse events, and on the window rather than the
    // canvas. Pointer events are the only family that covers a mouse, a
    // trackpad, a pen and a touchscreen with one code path — a tap on a
    // touchscreen is not guaranteed to synthesise a `mousedown` at all — and
    // listening on the window means a press is seen even if something ends up
    // layered over the canvas. Which press counts as a world click is then
    // decided by the target, once, below.
    let lastX = 0;
    let lastY = 0;

    /** A press on the canvas is a world order; a press on the HUD is not. */
    const isWorld = (e) => e.target === canvas;

    window.addEventListener("pointerdown", (e) => {
        lastX = e.clientX;
        lastY = e.clientY;
        if (!isWorld(e)) return;

        if (e.button === 0) {
            input.clickPending = true;
            input.clickX = e.clientX;
            input.clickY = e.clientY;
            input.clickDouble = e.detail >= 2;
        } else if (e.button === 2) {
            input.orbiting = true;
            // Capture, so a drag that runs off the canvas keeps turning the
            // camera instead of stopping at the edge of the window.
            try {
                canvas.setPointerCapture(e.pointerId);
            } catch {
                // Capture is a convenience; the window listener still works.
            }
        }
    });

    window.addEventListener("pointermove", (e) => {
        const dx = e.movementX ?? e.clientX - lastX;
        const dy = e.movementY ?? e.clientY - lastY;
        lastX = e.clientX;
        lastY = e.clientY;
        if (!input.orbiting) return;
        input.lookX += dx * LOOK_SCALE;
        input.lookY += dy * LOOK_SCALE;
    });

    const endOrbit = (e) => {
        if (e.button !== undefined && e.button !== 2 && e.type === "pointerup") return;
        input.orbiting = false;
    };
    window.addEventListener("pointerup", endOrbit);
    window.addEventListener("pointercancel", endOrbit);
    // The button can come up outside the window entirely.
    window.addEventListener("blur", endOrbit);

    canvas.addEventListener("contextmenu", (e) => e.preventDefault());

    canvas.addEventListener(
        "wheel",
        (e) => {
            e.preventDefault();
            input.zoomDelta += e.deltaY * 0.0016;
        },
        { passive: false }
    );

    window.addEventListener("keydown", (e) => {
        // The settings overlay lives on the backtick alone: F1 is hotbar slot 1.
        if (e.code === "Backquote" && !typing(e)) {
            e.preventDefault();
            hooks.onToggleOverlay?.();
            return;
        }
        if (e.code === "Escape") {
            hooks.onEscape?.();
            return;
        }
        if (typing(e)) return;

        const slot = HOTBAR_KEYS[e.code];
        if (slot) {
            e.preventDefault();
            if (!e.repeat) input.hotbarPressed = slot;
            return;
        }

        if (e.repeat) return;
        keys[e.code] = true;

        const modal = MODAL_KEYS[e.code];
        if (modal) {
            hooks.onModal?.(modal);
            return;
        }
        if (e.code === "Enter" || e.code === "NumpadEnter") {
            hooks.onChat?.();
            return;
        }

        const n = SPELL_KEYS[e.code];
        if (n) {
            input.spellPressed = n;
            if (n === 2) input.spellHeld2 = true;
        }
    });

    window.addEventListener("keyup", (e) => {
        keys[e.code] = false;
        if (SPELL_KEYS[e.code] === 2) input.spellHeld2 = false;
    });

    window.addEventListener("blur", () => {
        for (const k in keys) keys[k] = false;
        input.orbiting = false;
        input.surf = false;
        input.spellHeld2 = false;
    });
}

const SPELL_KEYS = {
    Digit1: 1,
    Digit2: 2,
    Digit3: 3,
    Digit4: 4,
    Digit5: 5,
};

const HOTBAR_KEYS = {
    F1: 1, F2: 2, F3: 3, F4: 4, F5: 5, F6: 6,
    F7: 7, F8: 8, F9: 9, F10: 10, F11: 11, F12: 12,
};

const MODAL_KEYS = {
    KeyI: "inventory",
    KeyC: "character",
    KeyK: "skills",
    KeyU: "quests",
    KeyP: "system",
};

/** Resolve held keys. Called once per frame before update. */
export function pollInput() {
    input.surf = !!keys.KeyR;
}

/**
 * Take the pending click, if there is one.
 *
 * Discrete events are consumed on read rather than cleared at the end of the
 * frame. The frame reads its orders first and clears its accumulators last, so
 * anything cleared on the frame boundary loses every event that arrived in
 * between — which, for a click read at the top of the frame, is very nearly all
 * of them.
 *
 * @returns {{x:number, y:number, double:boolean}|null}
 */
export function takeClick() {
    if (!input.clickPending) return null;
    input.clickPending = false;
    return { x: input.clickX, y: input.clickY, double: input.clickDouble };
}

/** Take the hotbar slot pressed since the last call, 0 for none. */
export function takeHotbar() {
    const slot = input.hotbarPressed;
    input.hotbarPressed = 0;
    return slot;
}

/**
 * Clear the per-frame accumulators. Called at the very end of the frame.
 *
 * Only continuous quantities live here: they are integrated every frame, so a
 * late arrival simply lands in the next one.
 */
export function endFrame() {
    input.lookX = 0;
    input.lookY = 0;
    input.zoomDelta = 0;
}

export function isDown(code) {
    return !!keys[code];
}

/**
 * On-screen input readout, for when the pointer is not doing what it should.
 *
 * Enabled with `?inputdebug` on the URL. It reports what the browser actually
 * delivered — the event type, the button, and crucially *what element it landed
 * on*, which is the difference between "the click never happened" and "the
 * click hit something layered over the canvas".
 *
 * @param {HTMLCanvasElement} canvas
 */
export function attachInputDebug(canvas) {
    const el = document.createElement("div");
    el.style.cssText =
        "position:fixed;top:8px;left:50%;transform:translateX(-50%);z-index:300;" +
        "background:rgba(2,6,23,.92);border:1px solid #f59e0b;border-radius:8px;" +
        "padding:8px 12px;font:12px/1.5 ui-monospace,Menlo,monospace;color:#fde68a;" +
        "pointer-events:none;white-space:pre;max-width:92vw;overflow:hidden";
    el.textContent = "input debug: waiting for an event…";
    document.body.appendChild(el);

    const describe = (t) => (t ? `${t.tagName}#${t.id || "-"}.${t.className || "-"}` : "null");
    let count = 0;

    const log = (e) => {
        count++;
        const under = document.elementFromPoint(e.clientX ?? 0, e.clientY ?? 0);
        el.textContent =
            `#${count} ${e.type} button=${e.button ?? "-"} pointerType=${e.pointerType ?? "-"}\n` +
            `target      ${describe(e.target)}\n` +
            `under cursor ${describe(under)}   canvas=${e.target === canvas}\n` +
            `clickPending=${input.clickPending} orbiting=${input.orbiting} ` +
            `look=${input.lookX.toFixed(3)} zoom=${input.zoomDelta.toFixed(3)}`;
    };

    for (const type of ["pointerdown", "pointerup", "mousedown", "click", "touchstart", "wheel"]) {
        window.addEventListener(type, log, { capture: true, passive: true });
    }
}
